import { useState, useEffect } from 'react';

/**
 * Hook to detect if the user is on a mobile device
 * Uses a combination of touch capability (maxTouchPoints) and viewport width
 * for reliable mobile detection.
 *
 * Detection logic:
 * - Device has touch support (navigator.maxTouchPoints > 0)
 * - AND viewport width < 1024px
 *
 * This approach correctly identifies:
 * - Mobile phones and tablets as mobile ✓
 * - Touch-enabled laptops/desktops as desktop ✓
 * - Desktop computers as desktop ✓
 *
 * @returns boolean indicating if the user is on a mobile device
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const isMobile = useIsMobile();
 *
 *   return (
 *     <div>
 *       {isMobile ? <MobileView /> : <DesktopView />}
 *     </div>
 *   );
 * };
 * ```
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/maxTouchPoints
 */
export const useIsMobile = (): boolean => {
    const checkIsMobile = (): boolean => {
        // Check if window is defined (SSR safety)
        if (typeof window === 'undefined' || !window.navigator) {
            return false;
        }

        // Check for touch capability combined with viewport width
        // This is more reliable than user agent detection
        const hasTouchScreen = window.navigator.maxTouchPoints > 0;
        const isMobileViewport = window.innerWidth < 1024; // Tablets and phones typically < 1024px

        // Both conditions must be true to be considered mobile
        return hasTouchScreen && isMobileViewport;
    };

    const [isMobile, setIsMobile] = useState<boolean>(checkIsMobile);

    useEffect(() => {
        // Update on mount to ensure correct value after SSR
        setIsMobile(checkIsMobile());

        // Listen to resize events as viewport width can change
        const handleResize = () => setIsMobile(checkIsMobile());

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return isMobile;
};
